/**
 * ThemedTable Component
 * 
 * Table that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { useState } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedTableProps, ThemedTableColumn } from '../../types/immersive-theme.types';
import {
  neumorphismTable,
  neumorphismTableHeader,
  neumorphismTableRow,
  neumorphismTableRowHover,
  neumorphismTableCell,
} from './theme-styles/neumorphism';
import {
  brutalistTable,
  brutalistTableHeader,
  brutalistTableRow,
  brutalistTableRowHover,
  brutalistTableCell,
  brutalistTableCellLast,
} from './theme-styles/brutalist';
import {
  retroTerminalTable,
  retroTerminalTableHeader,
  retroTerminalTableRow,
  retroTerminalTableRowHover,
  retroTerminalTableCell,
  retroTerminalTableCellLast,
} from './theme-styles/retro-terminal';
import {
  organicNatureTable,
  organicNatureTableHeader,
  organicNatureTableRow,
  organicNatureTableRowHover,
  organicNatureTableRowStriped,
  organicNatureTableCell,
} from './theme-styles/organic-nature';
import {
  luxuryGoldTable,
  luxuryGoldTableHeader,
  luxuryGoldTableRow,
  luxuryGoldTableRowHover,
  luxuryGoldTableCell,
} from './theme-styles/luxury-gold';

// Table container styles per theme
const tableStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#ffffff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTable,
  [ImmersiveThemeType.BRUTALIST]: brutalistTable,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTable,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTable,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTable,
};

// Header styles per theme
const headerStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    background: '#f9fafb',
    padding: '12px 16px',
    fontWeight: 600,
    textAlign: 'left',
    borderBottom: '1px solid #e5e7eb',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTableHeader,
  [ImmersiveThemeType.BRUTALIST]: brutalistTableHeader,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTableHeader,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTableHeader,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTableHeader,
};

// Row styles per theme
const rowStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    borderBottom: '1px solid #e5e7eb',
    transition: 'background 0.15s ease',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTableRow,
  [ImmersiveThemeType.BRUTALIST]: brutalistTableRow,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTableRow,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTableRow,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTableRow,
};

// Row hover styles per theme
const rowHoverStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { background: '#f9fafb' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTableRowHover,
  [ImmersiveThemeType.BRUTALIST]: brutalistTableRowHover,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTableRowHover,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTableRowHover,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTableRowHover,
};

// Cell styles per theme
const cellStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    padding: '12px 16px',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismTableCell,
  [ImmersiveThemeType.BRUTALIST]: brutalistTableCell,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalTableCell,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureTableCell,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldTableCell,
};

// Sort icon component
const SortIcon: React.FC<{ direction?: 'asc' | 'desc'; active: boolean; theme: ImmersiveThemeType }> = ({ 
  direction, 
  active,
  theme 
}) => {
  const color = active 
    ? (theme === ImmersiveThemeType.RETRO_TERMINAL ? 'var(--term-text)' : 'currentColor')
    : 'rgba(128, 128, 128, 0.5)';

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '4px', verticalAlign: 'middle' }}>
      <svg width="8" height="8" viewBox="0 0 8 8" style={{ opacity: direction === 'asc' && active ? 1 : 0.3 }}>
        <path d="M4 0L8 4H0L4 0Z" fill={color} />
      </svg>
      <svg width="8" height="8" viewBox="0 0 8 8" style={{ opacity: direction === 'desc' && active ? 1 : 0.3, marginTop: '2px' }}>
        <path d="M4 8L0 4H8L4 8Z" fill={color} />
      </svg>
    </span>
  );
};

// Loading skeleton row
const SkeletonRow: React.FC<{ columns: number; theme: ImmersiveThemeType }> = ({ columns, theme }) => {
  const skeletonColor = theme === ImmersiveThemeType.RETRO_TERMINAL 
    ? 'var(--term-bg-secondary)' 
    : theme === ImmersiveThemeType.LUXURY_GOLD
      ? 'var(--lux-bg-secondary)'
      : '#e5e7eb';

  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} style={cellStyles[theme]}>
          <div 
            style={{ 
              height: '16px', 
              background: skeletonColor, 
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }} 
          />
        </td>
      ))}
    </tr>
  );
};

export const ThemedTable: React.FC<ThemedTableProps> = ({
  columns,
  data,
  onRowClick,
  loading = false,
  emptyMessage = 'Nenhum dado encontrado',
  striped = false,
  hoverable = true,
  bordered = false,
  compact = false,
  stickyHeader = false,
  maxHeight,
  sortColumn,
  sortDirection,
  onSort,
  className,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  // Handle sort click
  const handleSort = (column: ThemedTableColumn) => {
    if (!column.sortable || !onSort) return;
    
    const newDirection = sortColumn === column.key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(column.key, newDirection);
  };

  // Compute container styles
  const containerStyles: React.CSSProperties = {
    ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}),
    ...(bordered && theme === ImmersiveThemeType.BRUTALIST ? { border: 'var(--brut-border)' } : {}),
    borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '8px',
    overflow: 'hidden',
  };

  // Compute table styles
  const computedTableStyles: React.CSSProperties = {
    ...tableStyles[theme],
    ...(compact ? { fontSize: '13px' } : {}),
    ...(reducedMotion ? { transition: 'none' } : {}),
  };

  // Compute header styles
  const computedHeaderStyles = (column: ThemedTableColumn): React.CSSProperties => ({
    ...headerStyles[theme],
    ...(column.width ? { width: column.width } : {}),
    ...(column.align ? { textAlign: column.align } : {}),
    ...(column.sortable ? { cursor: 'pointer', userSelect: 'none' } : {}),
    ...(stickyHeader ? { position: 'sticky', top: 0, zIndex: 10 } : {}),
    ...(compact ? { padding: '8px 12px' } : {}),
  });

  // Compute row styles
  const computedRowStyles = (index: number): React.CSSProperties => ({
    ...rowStyles[theme],
    ...(hoverable && hoveredRow === index ? rowHoverStyles[theme] : {}),
    ...(striped && index % 2 === 1 && theme === ImmersiveThemeType.ORGANIC_NATURE ? organicNatureTableRowStriped : {}),
    ...(striped && index % 2 === 1 && theme !== ImmersiveThemeType.ORGANIC_NATURE ? { background: 'rgba(0, 0, 0, 0.02)' } : {}),
    ...(onRowClick ? { cursor: 'pointer' } : {}),
    ...(reducedMotion ? { transition: 'none' } : {}),
  });

  // Compute cell styles
  const computedCellStyles = (column: ThemedTableColumn, isLast: boolean): React.CSSProperties => ({
    ...cellStyles[theme],
    ...(column.align ? { textAlign: column.align } : {}),
    ...(isLast && theme === ImmersiveThemeType.BRUTALIST ? brutalistTableCellLast : {}),
    ...(isLast && theme === ImmersiveThemeType.RETRO_TERMINAL ? retroTerminalTableCellLast : {}),
    ...(compact ? { padding: '8px 12px' } : {}),
  });

  // Empty state message with theme styling
  const emptyMessageStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px 16px',
    color: theme === ImmersiveThemeType.RETRO_TERMINAL ? 'var(--term-text-dim)' : 'var(--color-text-secondary, #6b7280)',
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { 
      fontFamily: 'var(--term-font)', 
      textShadow: 'var(--term-text-shadow)',
    } : {}),
  };

  return (
    <div style={containerStyles} className={className}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      
      <table style={computedTableStyles}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={column.key}
                style={computedHeaderStyles(column)}
                onClick={() => handleSort(column)}
                role={column.sortable ? 'button' : undefined}
                tabIndex={column.sortable ? 0 : undefined}
                onKeyDown={column.sortable ? (e) => e.key === 'Enter' && handleSort(column) : undefined}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {theme === ImmersiveThemeType.RETRO_TERMINAL && index === 0 && '> '}
                  {column.header}
                  {column.sortable && (
                    <SortIcon 
                      direction={sortColumn === column.key ? sortDirection : undefined}
                      active={sortColumn === column.key}
                      theme={theme}
                    />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        
        <tbody>
          {loading ? (
            // Loading skeleton
            Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} columns={columns.length} theme={theme} />
            ))
          ) : data.length === 0 ? (
            // Empty state
            <tr>
              <td colSpan={columns.length} style={emptyMessageStyles}>
                {theme === ImmersiveThemeType.RETRO_TERMINAL ? `> ${emptyMessage.toUpperCase()}` : emptyMessage}
              </td>
            </tr>
          ) : (
            // Data rows
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={computedRowStyles(rowIndex)}
                onClick={() => onRowClick?.(row, rowIndex)}
                onMouseEnter={() => hoverable && setHoveredRow(rowIndex)}
                onMouseLeave={() => hoverable && setHoveredRow(null)}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => e.key === 'Enter' && onRowClick(row, rowIndex) : undefined}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={column.key}
                    style={computedCellStyles(column, colIndex === columns.length - 1)}
                  >
                    {column.render 
                      ? column.render(row[column.key], row, rowIndex)
                      : String(row[column.key] ?? '')
                    }
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ThemedTable;
